import { push } from "connected-react-router";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  APPLICATION_MODE_NEW,
  DOCUMENT_CONTENT_TYPE_PDF,
  DOCUMENT_TYPE_CRIMINAL_RECORD,
  individualFileSizeAllowedInMB,
  LICENCE_SUB_TYPE_INDIVIDUAL_COMPLIANCE_OFFICER_APPLICATION,
  LICENCE_SUB_TYPE_INDIVIDUAL_COMPLIANCE_OFFICER_RETENTION_APPLICATION,
  REQUEST_STATUS,
  ROUTE_PATH,
} from "../../../app/constants";
import {
  Header,
  Button,
  NavigationLinkButton,
  Paragraph,
  ErrorMark,
} from "../../../components/RegularComponents";
import TopNavigationBar from "../../TopNavigationBar";
import { ERROR_STRING } from "../../../app/errors";
import { setAgencyCriminalRecordsDocuments } from "../../../redux/slice/primaryAgencyApplicationSlice";
import {
  fileNameWithLengthLimit,
  fileTypeEnum,
  extractBase64,
  validateFileName,
  acceptedFileFormats,
  convertFileDataForUrl,
} from "../../../app/documentUploadUtils";
import { saveBusinessLicenceApplicationData } from "../../../redux/slice/businessLicenceApplicationSlice";
import {
  DocumentViewerFileNameLink,
  DownloadFileNameLink,
} from "../../../components/DocumentViewer/DocumentFileComponent";
import { fetchDocumentData } from "../../../redux/slice/supportingDocumentsSlice";
import DocumentViewer from "../../../components/DocumentViewer/DocumentViewer";
import { applicationTypeFromLicenceSubType } from "../../../redux/slice/applicationsUtils";
import { usePathApplication } from "../../../redux/hooks/usePathApplication";
import { agencyApplicationSelector } from "../../../redux/selector/agencyApplicationSelector";

export const MainAgentCriminalRecordCheckUpload = (props) => {
  const { application } = usePathApplication();
  // const isPrincipalBroker =  (ROUTE_PATH.PB_APP.search(application) !== -1);
  
  const {agencyApplication} = useSelector(agencyApplicationSelector) || {};
  const licencingSubType = agencyApplication?.licencingSubType;
  const applicationType = applicationTypeFromLicenceSubType(licencingSubType);
  
  // This will change to primaryAgent when branch is pushed
  const serverSaveRequest = useSelector(state => state.primaryAgencyApplication.serverRequestStatus)


  // array of objects with a name and file
  const [criminalRecordCheckDetails, setCriminalRecordCheckDetails] = useState({});

  const [showValidationError, setShowValidationError] = useState(false);
  const [applicationSaveError, setApplicationSaveError] = useState(false);
  const [errorString, setErrorString] = useState("")

  const { isModal = false, onEditSave = () => {} } = props;

  const dispatch = useDispatch();

  const applicationMode = APPLICATION_MODE_NEW;
  const [openDocumentViewer, setOpenDocumentViewer] = useState(false);
  const [documentViewerDocument, setDocumentViewerDocument] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    const uploadCriminalRecordCheck = agencyApplication.documents?.filter(
      (docs) =>
        docs.documentType === DOCUMENT_TYPE_CRIMINAL_RECORD &&
        docs.changeType !== "DELETE"
    );

    if( uploadCriminalRecordCheck.length > 0) { 
      setCriminalRecordCheckDetails(uploadCriminalRecordCheck[0])
    }
  }, [
    agencyApplication,
    setCriminalRecordCheckDetails
  ])

  const validateData = () => {
    if(Object.keys(criminalRecordCheckDetails).length === 0 ) {
      setErrorString(ERROR_STRING.AT_LEAST_ONE_FILE_MISSING_ERROR) 
      return false
    }

    return true
  }

  const handleSaveAndNext = () => {
    if (!validateData()) {
      setShowValidationError(true);
      return;
    }

    setShowValidationError(false);

    dispatch(
      setAgencyCriminalRecordsDocuments(
        [criminalRecordCheckDetails]
      )
    );

    dispatch(
      saveBusinessLicenceApplicationData(applicationType, applicationMode)
    )
      .then((data) => {
        // On to the next item
        if(licencingSubType === LICENCE_SUB_TYPE_INDIVIDUAL_COMPLIANCE_OFFICER_APPLICATION || 
          licencingSubType === LICENCE_SUB_TYPE_INDIVIDUAL_COMPLIANCE_OFFICER_RETENTION_APPLICATION
        ) {
          dispatch(push(`/${application}${ROUTE_PATH.MRA_ADD_COMPLIANCE_OFFICER_DUTIES_CONFIRMATION}`));
        }
        else {
          dispatch(push(`/${application}${ROUTE_PATH.MRA_ADD_MAIN_AGENT_AGENT_DUTIES_CONFIRMATION}`));
        }
      })
      .catch((err) => {
        setApplicationSaveError(true);
      });
  };

  const handleSaveAndClose = () => {
    if (!validateData()) {
      setErrorString(ERROR_STRING.AT_LEAST_ONE_FILE_MISSING_ERROR)
      setShowValidationError(true);
      return;
    }

    setShowValidationError(false);

    dispatch(
      setAgencyCriminalRecordsDocuments(
        criminalRecordCheckDetails.map((criminalRecord) => {
          return criminalRecord.file;
        })
      )
    ); 

    onEditSave();
  };

  const fileSizeReplacement = (string, fileSize) => {
    const replaceRegExp = /<file_size>/g;
    const errorString = string.replace(replaceRegExp, fileSize + "MB");
    return errorString;
  };

  const fileComponent = () => {
    const file = criminalRecordCheckDetails;
    
    if(Object.keys(criminalRecordCheckDetails).length === 0) {
      return null;
    }

    return (
      <div className="supportingDocumentsFileContentWrapper bottomMarginMedium">
        <span className="fileName">
          {file.documentContentType === DOCUMENT_CONTENT_TYPE_PDF ? (
            <DocumentViewerFileNameLink
              id={"supporting-document-view-file-" + file.filename}
              key={"supporting-document-view-file-" + file.filename}
              file={file}
              onClick={() => {
                if (file.data) {
                  let fileDataUrl = convertFileDataForUrl(
                    file.data,
                    file.documentContentType
                  );
                  setDocumentViewerDocument(fileDataUrl);
                  setOpenDocumentViewer(true);
                  setSelectedFile(file);
                } else {
                  // download data and then view it.
                  if (file.guid) {
                    dispatch(fetchDocumentData(file.guid))
                      .then((downloadFile) => {
                        if (downloadFile) {
                          let fileDataUrl = convertFileDataForUrl(
                            downloadFile.data,
                            downloadFile.documentContentType
                          );
                          setDocumentViewerDocument(fileDataUrl);
                          setOpenDocumentViewer(true);
                          setSelectedFile(downloadFile);
                        }
                      })
                      .catch((error) => {
                        console.error(error);
                      });
                  }
                }
              }}
            />
          ) : (
            <DownloadFileNameLink
              id={"supporting-document-download-file-" + file.filename}
              componentKey={
                "supporting-document-download-file-" + file.filename
              }
              file={file}
            />
          )}
        </span>

        <NavigationLinkButton
          id={"criminal-record-check-document-remove-link"}
          text={"- Remove this file"}
          onClick={() => {
            setCriminalRecordCheckDetails({});
          }}
        />
      </div>
    );
  };

  const CriminalRecordBlock = () => {
    return (
      <div className="row">
        <div className="col-lg-8">
          {criminalRecordCheckDetails &&
          Object.keys(criminalRecordCheckDetails).length >
            0 ? (
            <div className="row">
              <div className="col-xs-12 col-lg-8">
                {fileComponent()}
              </div>
            </div>
          ) : (
            <div className="row">
              <div className="col-xs-12 col-lg-8">
                <div className="row">
                  <div className="col-xs-12">
                    <fieldset
                      className="ns-fieldset"
                      id="criminal-records-form"
                    >
                      <div className={"ns-fieldset"}>
                        <div className="ns-file-input">
                          <div className="row">
                            <div className="col-xs-12">Upload a file</div>
                          </div>
                          <div className="row">
                            <div className="col-xs-4">
                              <label
                                className="btn btn-primary"
                                id="upload-criminal-record-check-file-label"
                                htmlFor={`criminal-records-form-id`}
                              >
                                Choose File
                              </label>
                              <input
                                className="ns-input"
                                style={{display: "none"}}
                                type="file"
                                id={`criminal-records-form-id`}
                                name={`criminal-records-form-id`}
                                accept={acceptedFileFormats(
                                  DOCUMENT_TYPE_CRIMINAL_RECORD
                                ).join(",")}
                                multiple={false}
                                value=""
                                onInput={(e) => {
                                  if (e.target.files.length > 0) {
                                    let file = e.target.files[0];

                                    // individual file size can not be more than 15 MB
                                    if (
                                      file.size >
                                      individualFileSizeAllowedInMB * 2 ** 20
                                    ) {
                                      alert(
                                        fileSizeReplacement(
                                          ERROR_STRING.SUPPORTING_DOC_VERY_LARGE_INDIVIDUAL_FILE,
                                          individualFileSizeAllowedInMB
                                        )
                                      );
                                      return;
                                    } else if (!validateFileName(file.name)) {
                                      alert(
                                        "File name is not valid. Please select another file."
                                      );
                                      return;
                                    }

                                    const reader = new FileReader();
                                    reader.onload = (readerData) => {
                                      const criminalRecordCheckDetailsUpdate = {
                                        filename: fileNameWithLengthLimit(
                                          file.name
                                        ),
                                        documentContentType: fileTypeEnum(
                                          file.type
                                        ),
                                        data: extractBase64(
                                          readerData.target.result
                                        ),
                                        changeType: "SAVE",
                                        documentType:
                                          DOCUMENT_TYPE_CRIMINAL_RECORD,
                                        fileSize: file.size,
                                      };

                                      setCriminalRecordCheckDetails(
                                        criminalRecordCheckDetailsUpdate
                                      );
                                    };
                                    reader.readAsDataURL(file);

                                    // reset validation errors
                                    setShowValidationError(false);
                                  }
                                }}
                              />
                            </div>
                            <div style={{ paddingTop: "5px", marginLeft: "-35px" }} className="col-xs-7">
                              No file choosen
                            </div>
                          </div>
                        </div>
                      </div>
                    </fieldset>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="col-lg-2">
          {(Object.keys(setCriminalRecordCheckDetails).length > 0) && (
            <NavigationLinkButton
              id={"branch-remove-link"}
              text={"- Remove record"}
              onClick={() => {
                setCriminalRecordCheckDetails({});
              }}
            />
          )}
        </div>
      </div>
    );
  };

  const title = 'Upload your criminal record check document';
  
  return (
    <>
      <div className={!isModal ? "container" : ""}>
        {!isModal && (
          <>
            <TopNavigationBar  {...props} applicationType={applicationType} applicationMode={applicationMode} />
            <div className="row">
              <div className="col-xs-12 col-lg-8">
                <Header
                  style={{ paddingBottom: "30px" }}
                  id="company-fiscal-end-date-header"
                  header={title}
                />
                <Paragraph
                  style={{ paddingBottom: "10px" }}
                  id="company-fiscal-end-date-header"
                  text={[
                    "An applicant for a licence or renewal must obtain a criminal record check from the Royal Canadian Mounted Police, a Canadian municipal police force or the Canadian military police that is dated no earlier than 3 months before the date of application.",
                    "Criminal record checks will not be accepted from third party providers."
                  ]}
                />
              </div>
            </div>
          </>
        )}

        <CriminalRecordBlock data={criminalRecordCheckDetails} />

        <div className="row">
          <div className="col-xs-12 col-lg-8">
            <Paragraph
              style={{ paddingBottom: "20px" }}
              id="upload-mortgage-education-text"
              text={[
                "The maximum file size allowed is 15 MB per file and 50 MB total. Supported document types are Microsoft Word, Microsoft Office Excel, or Adobe PDF.  All digital files must be in an uncompressed format, i.e no zip or ARC files.",
              ]}
            />
          </div>
        </div>

        <div className="row">
          <div
            className={
              "col-lg-4" + (showValidationError || applicationSaveError)
                ? " has-error"
                : ""
            }
          >
            <Button
              id="company-fiscal-end-date-button"
              style={{ margin: "0 10px 10px 0" }}
              buttonType={"regular_primary"}
              text={!isModal ? "Save and next" : "Save and close"}
              showSpinner={!applicationSaveError && 
                            (serverSaveRequest === REQUEST_STATUS.IN_PROGRESS)}
              onClick={!isModal ? handleSaveAndNext : handleSaveAndClose}
              disabled={(serverSaveRequest === REQUEST_STATUS.IN_PROGRESS)}
            />
            {(applicationSaveError || showValidationError) && ErrorMark()}
            {showValidationError && (
              <div className="ns-error-message">
                <span className="ns-visually-hidden">Error:</span>
                {errorString}
              </div>
            )}
            {applicationSaveError && (
              <div className="ns-error-message">
                <span className="ns-visually-hidden">Error:</span>
                {ERROR_STRING.APPLICATION_SAVE_ERROR}
              </div>
            )}
          </div>
        </div>

        <DocumentViewer
          fileData={documentViewerDocument}
          isOpen={openDocumentViewer}
          fileName={selectedFile?.filename}
          handleClose={() => setOpenDocumentViewer(false)}
        />
      </div>
    </>
  );
};
